import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import type {
  ScheduleType,
  ScheduledJobType
} from '../../../api/campaignScheduledJobs';
import type { ScheduledJobItem } from './index';
import CellEnrichmentScheduleSummary, {
  parseCellSpecsFromJobConfig
} from './CellEnrichmentScheduleSummary';
import HubSpotImportMappingSummary, {
  getHubSpotSourceConfigFromJobConfig
} from './HubSpotImportMappingSummary';

interface EditScheduleDialogProps {
  open: boolean;
  job: ScheduledJobItem | null;
  scheduleType: ScheduleType;
  onScheduleTypeChange: (next: ScheduleType) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  onUpdateCellsFromSelection: () => void;
  onEditHubSpotImportMapping?: (job: ScheduledJobItem) => void;
  scheduleOptions: { value: ScheduleType; label: string }[];
}

function jobTypeLabel(jobType: ScheduledJobType): string {
  switch (jobType) {
    case 'campaign_prospect_ingestion':
      return 'CRM import';
    case 'campaign_cell_execution':
      return 'Cell enrichment';
    case 'campaign_crm_synchronization':
      return 'CRM sync (export)';
    default:
      return jobType;
  }
}

const EditScheduleDialog: React.FC<EditScheduleDialogProps> = ({
  open,
  job,
  scheduleType,
  onScheduleTypeChange,
  onClose,
  onSave,
  saving,
  onUpdateCellsFromSelection,
  onEditHubSpotImportMapping,
  scheduleOptions
}) => {
  const isHubSpotImport =
    job?.jobType === 'campaign_prospect_ingestion' &&
    (job.config as { source_type?: string })?.source_type === 'hubspot_import';
  const hubSpotSourceConfig = isHubSpotImport
    ? getHubSpotSourceConfigFromJobConfig(
        job.config as Record<string, unknown>
      )
    : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {job ? jobTypeLabel(job.jobType) : 'Edit schedule'}
      </DialogTitle>
      <DialogContent>
        {job && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Schedule: {job.scheduleType}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Run count: {job.runCount}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Created:{' '}
              {job.createTs ? new Date(job.createTs).toLocaleString() : '—'}
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="edit-schedule-label">Schedule</InputLabel>
              <Select
                labelId="edit-schedule-label"
                value={scheduleType}
                label="Schedule"
                onChange={(e) =>
                  onScheduleTypeChange(e.target.value as ScheduleType)
                }
              >
                {scheduleOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isHubSpotImport && hubSpotSourceConfig && (
              <Box>
                <HubSpotImportMappingSummary sourceConfig={hubSpotSourceConfig} />
                {onEditHubSpotImportMapping && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => onEditHubSpotImportMapping(job)}
                    sx={(theme) => ({
                      mt: 1,
                      border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
                      color:
                        theme.customColors?.themePrimary ||
                        theme.palette.primary.main,
                      borderRadius: '50px',
                      textTransform: 'capitalize',
                      '&:hover': {
                        border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
                        backgroundColor:
                          (theme.customColors?.themePrimary ||
                            theme.palette.primary.main) + '20'
                      }
                    })}
                  >
                    Edit mapping
                  </Button>
                )}
              </Box>
            )}

            {job.jobType === 'campaign_cell_execution' && (
                <Box sx={{ mt: 2 }}>
                  <CellEnrichmentScheduleSummary
                    scheduleTypeLabel={scheduleType}
                    cellSpecs={parseCellSpecsFromJobConfig(job.config)}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={onUpdateCellsFromSelection}
                    sx={(theme) => ({
                      mt: 1,
                      border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
                      backgroundColor:
                        theme.customColors?.themePrimary ||
                        theme.palette.primary.main,
                      color: theme.customColors?.themeSecondary || 'white',
                      borderRadius: '50px',
                      textTransform: 'capitalize',
                      '&:hover': {
                        backgroundColor:
                          theme.customColors?.themePrimary ||
                          theme.palette.primary.main,
                        opacity: 0.9
                      }
                    })}
                  >
                    Update cells
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Closes this dialog. Select a new range in the spreadsheet,
                    then use Confirm in the Scheduling panel to save.
                  </Typography>
                </Box>
              )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
            backgroundColor: theme.customColors?.themeSecondary,
            color:
              theme.customColors?.themePrimary || theme.palette.primary.main,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:hover': {
              border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
              backgroundColor:
                theme.customColors?.themePrimary || theme.palette.primary.main,
              color: theme.customColors?.themeSecondary || 'white'
            }
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
            backgroundColor:
              theme.customColors?.themePrimary || theme.palette.primary.main,
            color: theme.customColors?.themeSecondary || 'white',
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor:
                theme.customColors?.themePrimary || theme.palette.primary.main,
              opacity: 0.9
            }
          })}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleDialog;
